<template>
  <v-menu
    ref="menu"
    v-model="menu"
    :close-on-content-click="false"
    transition="scale-transition"
    offset-y
    content-class="date-picker-menu"
    max-width="290px"
    min-width="auto"
  >
    <template v-slot:activator="{ on, attrs }">
      <div :title="dateTooltip">
        <v-text-field
          v-model="internalFormattedDate"
          :label="label"
          :placeholder="dateFormatHint"
          prepend-icon="mdi-calendar"
          v-bind="attrs"
          v-on="on"
          v-on:keyup.enter="onEnter"
          :clearable="!disableClearable"
          :rules="[validateDate, ...parsedRules]"
          :disabled="disabled"
          @blur="onBlur"
          @click:clear="onClear"
          @click:prepend="openMenu"
        ></v-text-field>
      </div>
    </template>
    <v-date-picker
      v-model="internalDate"
      no-title
      :locale="locale"
      :min="toISODate(min)"
      :max="toISODate(max)"
      @input="menu = false"
    ></v-date-picker>
  </v-menu>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import {
  formatDate as formatLocalizedDate,
  formatMediumDate,
  getDateFormatHint,
  parseDate as parseLocalizedDate,
} from "@/helpers/date-utils";

type DateModel = Temporal.PlainDate | string | null | undefined;
type DateRule = (date: Temporal.PlainDate | null) => boolean | string;

const asPlainDate = (date: DateModel): Temporal.PlainDate | null => {
  if (!date) return null;
  if (date instanceof Temporal.PlainDate) return date;

  try {
    return Temporal.PlainDate.from(date);
  } catch {
    return null;
  }
};

export default Vue.extend({
  name: "DatepickerFormatted",
  props: {
    date: [Temporal.PlainDate, String] as unknown as PropType<DateModel>,
    label: String,
    disabled: Boolean,
    disableClearable: Boolean,
    rules: Array,
    locale: String,
    min: [Temporal.PlainDate, String] as unknown as PropType<DateModel>,
    max: [Temporal.PlainDate, String] as unknown as PropType<DateModel>,
  },
  created: function () {
    this.internalFormattedDate = this.formatDate(this.date);
  },
  data: function () {
    return {
      menu: false,
      internalDate: asPlainDate(this.$props.date)?.toString() || "",
      internalFormattedDate: ''
    };
  },
  computed: {
    dateFormatHint() { return getDateFormatHint(this.locale); },
    dateTooltip(): string | undefined {
      const date = asPlainDate(this.internalDate);
      return date ? formatMediumDate(date, this.locale) : undefined;
    },
    parsedRules: function () {
      if (!!this.rules && !!this.rules.length) {
        return (this.rules as DateRule[]).map((rule) =>
          (value: string) => rule(parseLocalizedDate(value, this.locale))
        );
      } else {
        return [];
      }
    },
  },
  methods: {
    openMenu() {
      this.menu = true;
    },
    validateDate(dateValue: string) {
      if (dateValue === undefined || dateValue === "" || dateValue === null) return true;
      return this.parseDate(dateValue) ? true : "Invalid Date Format";
    },
    formatDate(date: DateModel) {
      const parsed = asPlainDate(date);
      return parsed ? formatLocalizedDate(parsed, this.locale) : "";
    },
    toISODate(date: DateModel): string | undefined {
      return asPlainDate(date)?.toString();
    },
    parseDate(date: string): Temporal.PlainDate | null {
      return parseLocalizedDate(date, this.locale);
    },
    getUpdateData() {
      return {
        formattedDate: this.internalFormattedDate,
        date: asPlainDate(this.internalDate),
      };
    },
    onEnter: async function() {
      const date = this.parseDate(this.internalFormattedDate);
      this.internalDate = date?.toString() || "";
      this.$emit("enter", {
        formattedDate: date ? this.formatDate(date) : this.internalFormattedDate,
        date,
      });
    },
    onBlur: function() {
      this.internalDate = this.parseDate(this.internalFormattedDate)?.toString() || "";
    },
    onClear: function() {
      this.internalDate = "";
      this.internalFormattedDate = "";
      this.$emit("updateDates", { formattedDate: "", date: null });
    }
  },
  watch: {
    date(val: DateModel) {
      this.internalDate = asPlainDate(val)?.toString() || "";
      this.internalFormattedDate = this.formatDate(val);
    },
    internalDate(val) {
      this.internalFormattedDate = this.formatDate(val);
      this.$emit("updateDates", this.getUpdateData());
    }
  },
});
</script>

<style>
@media (max-width: 599px) {
  .date-picker-menu {
    left: max(8px, calc(50vw - 145px)) !important;
    right: auto !important;
    transform-origin: center top !important;
  }
}
</style>
